import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import JsonLd from "@/components/base/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { categories } from "@/mocks/posts";

const ORIGIN = "https://jiuxiao666.cn";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "只整理公开信息",
    desc: "不传播非公开的个人信息、私人行程与未经授权的影像，所有内容来源都基于公开可查的物料与讨论。",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "明确标注信息属性",
    desc: "已确认的信息与仍在讨论中的说法会分开呈现，传闻类内容会清楚标注，不让读者误判。",
  },
  {
    icon: "ri-refresh-line",
    title: "后续进展及时补齐",
    desc: "话题有新的公开进展时，我们会在原内容中补充更新，让读者一次看到完整脉络而不是碎片。",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "理性吃瓜的基调",
    desc: "娱乐话题应该轻松有趣，我们用平实的语言整理内容，不煽动情绪，也不制造对立。",
  },
];

const testimonials = [
  {
    name: "蹲瓜的小鱼",
    role: "每天早八刷更新",
    text: "早高峰地铁上就把昨天漏掉的都补完了，时间线整理得很清楚，不用自己在各种帖子里翻。",
  },
  {
    name: "晚八点冲浪",
    role: "综艺爱好者",
    text: "综艺那块的复盘写得挺实在，会讲清楚剪辑和机制的影响，看完对节目理解更立体了。",
  },
  {
    name: "不吃生的瓜",
    role: "谨慎型读者",
    text: "最满意的是会标明哪些还没被证实，不用自己判断真假，这点对普通读者很友好。",
  },
];

const stats = [
  { value: "6", label: "常驻主题栏目" },
  { value: "每日 2 次", label: "重点内容更新" },
  { value: "600+", label: "已整理话题数量" },
  { value: "5 年", label: "娱乐内容整理经验" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 黑吃瓜网的定位与内容原则",
    description:
      "了解黑吃瓜网的品牌定位、内容原则与发展理念：只整理公开信息、明确标注信息属性、及时补齐后续进展，用理性轻松的方式陪你吃瓜。",
    keywords: "关于黑吃瓜网,内容原则,娱乐资讯定位,黑吃瓜网团队",
    path: "/about",
  });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于黑吃瓜网",
      url: `${ORIGIN}/about`,
      inLanguage: "zh-CN",
      description: "黑吃瓜网的品牌定位、内容原则与团队理念介绍。",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "黑吃瓜网",
      url: `${ORIGIN}/`,
      email: "hi@heichigua.example.com",
      description: "专注娱乐话题整理的吃瓜资讯站，提供今日爆料、新瓜汇总与热门猛料更新。",
    },
  ];

  return (
    <>
      <JsonLd data={jsonLd} />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading font-bold text-3xl md:text-5xl text-foreground-950 leading-tight max-w-3xl">
            我们想把吃瓜这件事，做得清爽一点
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-700 max-w-2xl leading-relaxed">
            黑吃瓜网是一个专注娱乐话题整理的资讯站。我们不做情绪放大器，只把公开的信息、网友的讨论以及后续进展，整理成有顺序、有标注、能快速读完的内容。
          </p>

          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((item) => (
              <div key={item.label} className="p-4 rounded-lg bg-background-50 border border-background-200">
                <p className="font-heading font-bold text-xl md:text-2xl text-primary-700">{item.value}</p>
                <p className="mt-1 text-xs md:text-sm text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wide text-accent-700 mb-2">
                <span className="w-4 h-[2px] bg-accent-500 inline-block"></span>
                品牌介绍
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-snug">
                一个为普通读者写的娱乐话题站
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                很多娱乐话题在传播过程中会被拆成无数条碎片：一条短视频、一句截图、一段没有前因后果的转发。读者想搞清楚事情的原委，往往要在多个平台之间来回跳转，最后还是一头雾水。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                黑吃瓜网做的就是把这些碎片重新拼回去。我们把同一个话题的公开信息集中在一篇文章里，标注清楚时间、来源与信息属性，并配上要点速览，让你在几分钟内就能掌握事情的来龙去脉。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                我们相信，吃瓜的乐趣在于了解，而不在于被情绪裹挟。所以我们的内容节奏是从容的，语气是平实的，立场是克制的。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <SectionHeading
            eyebrow="网站定位"
            title="我们覆盖哪些方向"
            desc="六个常驻栏目覆盖日常吃瓜的主要方向，每个栏目都有自己的整理逻辑与更新节奏。"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => (
              <div key={cat.slug} className="p-5 rounded-lg border border-background-200 bg-background-50">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${cat.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{cat.name}</h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <SectionHeading
            eyebrow="内容原则"
            title="四条我们一直坚持的规则"
            desc="这些原则决定了我们写什么、怎么写，也决定了哪些内容我们不会碰。"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {principles.map((item) => (
              <div key={item.title} className="flex gap-4 p-5 rounded-lg border border-background-200 bg-background-100">
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="font-heading font-bold text-base text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <SectionHeading eyebrow="用户口碑" title="读者怎么说" desc="来自订阅用户的真实反馈（昵称为读者自行设置）。" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((item) => (
              <figure key={item.name} className="p-5 rounded-lg border border-background-200 bg-background-50 flex flex-col">
                <div className="flex items-center gap-1 text-primary-500 mb-3">
                  {[0, 1, 2, 3, 4].map((star) => (
                    <i key={star} className="ri-star-fill text-sm" aria-hidden="true"></i>
                  ))}
                </div>
                <blockquote className="text-sm text-foreground-700 leading-relaxed flex-1">
                  「{item.text}」
                </blockquote>
                <figcaption className="mt-4 pt-4 border-t border-background-200">
                  <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500 mt-0.5">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-primary-950">想聊聊，或者想投稿？</h2>
              <p className="mt-3 text-sm md:text-base text-primary-800 max-w-xl leading-relaxed">
                无论是线索投递、内容更正，还是品牌合作，都可以通过联系表单找到我们。邮件：hi@heichigua.example.com
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                前往联系我们
              </Link>
              <Link
                to="/breaking"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-primary-300 bg-background-50 text-primary-800 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览今日爆料
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}